<template>
  <Listbox as="div" v-model="selected.categories" multiple class="z-20">
    <ListboxButton
      class="whitespace-nowrap flex items-center rounded-sm border-transparent hover:bg-sky-100 hover:text-sky-800 transition py-2 px-3 sm:text-sm"
      :class="{
        'bg-sky-100 text-sky-800': selected.categories.length > 0,
        'bg-gray-100 text-gray-900': selected.categories.length < 1,
      }"
    >
      <span class="flex items-center">
        {{
          selected.categories.length === 1
            ? selected.categories[0]?.name
            : t("admin.search.filters.category")
        }}
        <span
          v-if="selected.categories.length > 1"
          class="ml-1 inline-flex items-center rounded-full bg-sky-200 px-2 py-0.5 text-xs font-medium text-sky-800"
        >
          {{ selected.categories.length }}
        </span>
      </span>
      <span class="ml-2">
        <ChevronDownIcon
          v-if="selected.categories.length === 0"
          class="h-5 w-5 text-gray-900"
          aria-hidden="true"
        />
        <XMarkIcon
          v-else
          @click.prevent="clear"
          class="h-5 w-5 text-sky-800"
          aria-hidden="true"
        />
      </span>
    </ListboxButton>

    <transition
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <ListboxOptions
        class="z-10 absolute left-0 sm:left-auto w-full sm:w-auto z-10 mt-3 max-h-56 overflow-auto rounded-sm bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-sm"
      >
        <ListboxOption
          as="template"
          v-for="category in categories"
          :key="category.id"
          :value="category"
          v-slot="{ active, selected }"
        >
          <li
            :class="[
              active ? 'text-gray-900 bg-gray-100' : 'text-gray-900',
              'relative cursor-pointer select-none py-2 pl-3 pr-9',
            ]"
          >
            <div class="flex items-center space-x-2">
              <input
                class="h-4 w-4 rounded-xs border-gray-300 text-brand-900 focus:ring-0 focus:outline-0 focus:ring-offset-0 focus:ring-0"
                type="checkbox"
                :checked="selected"
              />
              <span
                :class="['truncate font-normal', selected && 'font-semibold']"
              >
                {{ category.name }}
              </span>
            </div>
          </li>
        </ListboxOption>
      </ListboxOptions>
    </transition>
  </Listbox>
</template>

<script setup>
import {
  defineEmits,
  defineProps,
  onBeforeMount,
  reactive,
  watch,
  watchEffect,
} from "vue";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/vue";
import { XMarkIcon, ChevronDownIcon } from "@heroicons/vue/20/solid";
import { useCategories } from "@/Tailwind/Mixins/useCategories";
import { flatten } from "lodash";
import { useEventBus } from "@/Tailwind/Mixins/useEventBus";

import { useI18n } from "vue-i18n";
const { t } = useI18n();

const emit = defineEmits(["change"]);

const props = defineProps({
  value: Array,
});

const categories = useCategories();
const selected = reactive({
  categories: [],
});

onBeforeMount(() => {
  if (!props.value) {
    return;
  }

  selected.categories =
    categories.filter((category) =>
      category.categoryIds.some((categoryId) =>
        props.value.includes(categoryId)
      )
    ) || [];
});

watchEffect(() =>
  emit(
    "change",
    flatten(selected.categories.map((category) => category.categoryIds))
  )
);

const clear = () => (selected.categories = []);

const { bus } = useEventBus();

watch(
  () => bus.value.get("clearFilters"),
  () => {
    clear();
  }
);
</script>
